import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  ArrowLeft, 
  Users, 
  Shield, 
  User,
  Link as LinkIcon,
  Unlink,
  Check,
  X,
  Globe
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Available sites for permissions
const AVAILABLE_SITES = [
  { value: 'cr', label: 'Cultural Revolution' },
  { value: 'selina', label: 'Selina Ringel' },
  { value: 'bethetrain', label: 'Be The Train' }
];

export const UserManagementPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linkingUser, setLinkingUser] = useState(null);
  const [selectedArtist, setSelectedArtist] = useState('');
  const [editingSitePermissions, setEditingSitePermissions] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersRes, artistsRes] = await Promise.all([
          fetch(`${API_URL}/api/admin/users`, { credentials: 'include' }),
          fetch(`${API_URL}/api/artists`, { credentials: 'include' })
        ]);

        if (usersRes.status === 403) {
          navigate('/cms');
          return;
        }

        if (usersRes.ok) setUsers(await usersRes.json());
        if (artistsRes.ok) setArtists(await artistsRes.json());
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role: newRole })
      });

      if (response.ok) {
        setUsers(users.map(u => 
          u.user_id === userId ? { ...u, role: newRole } : u
        ));
      }
    } catch (error) {
      console.error('Error updating role:', error);
    }
  };

  const handleLinkArtist = async (userId) => {
    if (!selectedArtist) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/link-artist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ artist_id: selectedArtist })
      });

      if (response.ok) {
        // Refresh data
        const usersRes = await fetch(`${API_URL}/api/admin/users`, { credentials: 'include' });
        if (usersRes.ok) setUsers(await usersRes.json());
        setLinkingUser(null);
        setSelectedArtist('');
      }
    } catch (error) {
      console.error('Error linking artist:', error);
    }
  };

  const handleUnlinkArtist = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/unlink-artist`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        // Refresh data
        const usersRes = await fetch(`${API_URL}/api/admin/users`, { credentials: 'include' });
        if (usersRes.ok) setUsers(await usersRes.json());
      }
    } catch (error) {
      console.error('Error unlinking artist:', error);
    }
  };

  const handleSitePermissionsChange = async (userId, site, isChecked) => {
    const user = users.find(u => u.user_id === userId);
    const currentPermissions = user?.site_permissions || ['cr'];
    
    let newPermissions;
    if (isChecked) {
      newPermissions = [...new Set([...currentPermissions, site])];
    } else {
      newPermissions = currentPermissions.filter(s => s !== site);
      // Ensure at least 'cr' is always selected
      if (newPermissions.length === 0) {
        newPermissions = ['cr'];
      }
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/site-permissions`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ site_permissions: newPermissions })
      });

      if (response.ok) {
        setUsers(users.map(u => 
          u.user_id === userId ? { ...u, site_permissions: newPermissions } : u
        ));
      }
    } catch (error) {
      console.error('Error updating site permissions:', error);
    }
  };

  // Get unlinked artists for dropdown
  const linkedArtistIds = users.filter(u => u.linked_artist).map(u => u.linked_artist?.artist_id);
  const unlinkedArtists = artists.filter(a => !linkedArtistIds.includes(a.artist_id));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="editorial-container py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/cms" className="text-xl editorial-serif">Cultural Revolution</Link>
            <span className="text-xs uppercase tracking-widest text-muted-foreground px-2 py-1 bg-muted">
              User Management
            </span>
          </div>
          <Link to="/cms">
            <Button variant="ghost" size="sm">
              <ArrowLeft size={16} className="mr-2" />
              Back to CMS
            </Button>
          </Link>
        </div>
      </header>

      <div className="editorial-container py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl editorial-serif mb-2">User Management</h1>
              <p className="text-muted-foreground">Manage user roles and link artist profiles</p>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Users size={16} />
              {users.length} users
            </div>
          </div>

          <div className="space-y-4">
            {users.map((user) => (
              <Card key={user.user_id} className="p-4" data-testid={`user-card-${user.user_id}`}>
                <div className="flex items-start gap-4">
                  {/* Avatar */}
                  <div className="w-12 h-12 rounded-full bg-muted overflow-hidden flex-shrink-0">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User size={24} className="text-muted-foreground" />
                      </div>
                    )}
                  </div>

                  {/* User Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium">{user.name}</h3>
                      {user.role === 'admin' && (
                        <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs uppercase">
                          Admin
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{user.email}</p>
                    
                    {/* Linked Artist */}
                    {user.linked_artist ? (
                      <div className="mt-2 flex items-center gap-2">
                        <LinkIcon size={12} className="text-green-500" />
                        <span className="text-xs text-muted-foreground">
                          Linked to: <span className="text-foreground">{user.linked_artist.name}</span>
                        </span>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 px-2"
                          onClick={() => handleUnlinkArtist(user.user_id)}
                        >
                          <Unlink size={12} />
                        </Button>
                      </div>
                    ) : (
                      <div className="mt-2">
                        {linkingUser === user.user_id ? (
                          <div className="flex items-center gap-2">
                            <select
                              value={selectedArtist}
                              onChange={(e) => setSelectedArtist(e.target.value)}
                              className="text-sm px-2 py-1 bg-background border border-border rounded"
                            >
                              <option value="">Select artist...</option>
                              {unlinkedArtists.map(a => (
                                <option key={a.artist_id} value={a.artist_id}>{a.name}</option>
                              ))}
                            </select>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-6 px-2"
                              onClick={() => handleLinkArtist(user.user_id)}
                              disabled={!selectedArtist}
                            >
                              <Check size={12} />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-6 px-2"
                              onClick={() => { setLinkingUser(null); setSelectedArtist(''); }}
                            >
                              <X size={12} />
                            </Button>
                          </div>
                        ) : (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 px-2 text-xs"
                            onClick={() => setLinkingUser(user.user_id)}
                          >
                            <LinkIcon size={12} className="mr-1" />
                            Link Artist
                          </Button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Role Selector */}
                  <div className="flex flex-col items-end gap-2">
                    <select
                      value={user.role}
                      onChange={(e) => handleRoleChange(user.user_id, e.target.value)}
                      className="px-3 py-1.5 bg-background border border-border rounded text-sm"
                      data-testid={`role-select-${user.user_id}`}
                    >
                      <option value="artist">Artist</option>
                      <option value="admin">Admin</option>
                    </select>
                    
                    {/* Site Permissions - Only show for non-admin users */}
                    {user.role !== 'admin' && (
                      <div className="text-right">
                        <button
                          onClick={() => setEditingSitePermissions(
                            editingSitePermissions === user.user_id ? null : user.user_id
                          )}
                          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                        >
                          <Globe size={12} />
                          Sites: {(user.site_permissions || ['cr']).length}
                        </button>
                        
                        {editingSitePermissions === user.user_id && (
                          <div className="mt-2 p-2 bg-muted/50 rounded text-left space-y-1">
                            {AVAILABLE_SITES.map(site => (
                              <label key={site.value} className="flex items-center gap-2 text-xs cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={(user.site_permissions || ['cr']).includes(site.value)}
                                  onChange={(e) => handleSitePermissionsChange(user.user_id, site.value, e.target.checked)}
                                  className="rounded"
                                />
                                {site.label}
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {user.role === 'admin' && (
                      <span className="text-xs text-muted-foreground italic">All sites</span>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {users.length === 0 && (
            <div className="text-center py-12">
              <Users size={48} className="mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">No users found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserManagementPage;
